import React, { useCallback, useMemo } from 'react'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { DocumentProspectingPageType } from '../../../models/DocumentProspecting'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import {
  resetDocumentProspecting,
  selectDocumentProspectingAttachment,
  selectDocumentProspectingBaseFormValues,
  selectDocumentProspectingBaseFormValuesLoading,
  selectDocumentProspectingSelectedFileName,
  selectDocumentProspectingUploadIsLoading,
  selectDocumentProspectingUploadedFileId,
  setDocumentProspectingPage,
} from '../../../store/processDocument/documentProspecting'

import { type IntegrationServiceId } from '../../../common/integrations'
import { type FileBrowserItem } from '../../../components/DocumentFileBrowser/types'

import { ProcessDocumentFileUploadProgress, ProgressItem, ProgressItemWrapper } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { ProcessDocumentFileInput } from '../ProcessDocumentFileInput/ProcessDocumentFileInput'
import { buildFileTooLargeToast, DOCUMENT_UPLOAD_SOURCE_LABEL, DocumentUploadSource, isFileSizeValid } from '../DocumentUpload/utils'
import { useMaxDocumentFileSizeBytes, useSafeTimeout } from '../hooks'

import { DocumentIntegrationBrowseButtons } from '../../../components/DocumentFileBrowser/DocumentIntegrationBrowseButtons'
import { useDocumentProspectingFileUpload, useProspectingBaseForm } from './hooks'

export const DocumentProspectingFileUpload = () => {
  const dispatch = useAppDispatch()

  const { tracked, trackingClient } = useTracking()

  const selectedFileName = useAppSelector(selectDocumentProspectingSelectedFileName)

  const { uploadFile, selectIntegrationFile } = useDocumentProspectingFileUpload()
  const { generateFormValues } = useProspectingBaseForm()
  const { setSafeTimeout, clearSafeTimeout } = useSafeTimeout()
  const { maxFileSizeBytes, accountHasZDRPolicy } = useMaxDocumentFileSizeBytes()

  const resetFileUpload = useCallback(() => {
    dispatch(resetDocumentProspecting())
  }, [dispatch])

  const _handleFilesSelected = useCallback(
    async (files: File[]) => {
      const file = files[0]
      if (!file) {
        return
      }

      if (!isFileSizeValid(file.size, maxFileSizeBytes)) {
        dispatch(addToast(buildFileTooLargeToast(accountHasZDRPolicy, maxFileSizeBytes, 'The file')))
        if (file.size > maxFileSizeBytes) {
          trackingClient.track({
            eventName: TrackEvent.DOCUMENT_PROSPECTING_FILE_TOO_LARGE,
            eventDetails: {
              fileSizeBytes: file.size,
              maxFileSizeBytes,
              source: DOCUMENT_UPLOAD_SOURCE_LABEL[DocumentUploadSource.ZOCKS],
            },
          })
        }
        return
      }

      clearSafeTimeout()

      const fileId = await uploadFile(file)
      if (!fileId) {
        resetFileUpload()
        return
      }

      const baseFormValues = await generateFormValues({ fileId })
      if (!baseFormValues) {
        resetFileUpload()
        return
      }

      setSafeTimeout(() => dispatch(setDocumentProspectingPage(DocumentProspectingPageType.PROSPECTING_STEPS)), 500)
    },
    [accountHasZDRPolicy, clearSafeTimeout, dispatch, generateFormValues, maxFileSizeBytes, resetFileUpload, setSafeTimeout, trackingClient, uploadFile],
  )

  const handleFilesSelected = tracked(_handleFilesSelected, { eventName: TrackEvent.DOCUMENT_PROSPECTING_FILE_ADDED })

  const _handleIntegrationFileSelected = useCallback(
    async (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => {
      const file = items.find((item) => item.type === 'file')
      if (!file) {
        return
      }

      if (file.size != null && !isFileSizeValid(file.size, maxFileSizeBytes)) {
        dispatch(addToast(buildFileTooLargeToast(accountHasZDRPolicy, maxFileSizeBytes, 'The file')))
        if (file.size > maxFileSizeBytes) {
          trackingClient.track({
            eventName: TrackEvent.DOCUMENT_PROSPECTING_FILE_TOO_LARGE,
            eventDetails: {
              fileSizeBytes: file.size,
              maxFileSizeBytes,
              source: DOCUMENT_UPLOAD_SOURCE_LABEL[DocumentUploadSource.INTEGRATION],
              serviceId,
            },
          })
        }
        return
      }

      clearSafeTimeout()

      const created = await selectIntegrationFile(serviceId, file)
      if (!created) {
        resetFileUpload()
        return
      }

      const baseFormValues = await generateFormValues({ source: 'integration', connector: serviceId, fileId: file.id })
      if (!baseFormValues) {
        resetFileUpload()
        return
      }

      setSafeTimeout(() => dispatch(setDocumentProspectingPage(DocumentProspectingPageType.PROSPECTING_STEPS)), 500)
    },
    [
      accountHasZDRPolicy,
      clearSafeTimeout,
      dispatch,
      generateFormValues,
      maxFileSizeBytes,
      resetFileUpload,
      selectIntegrationFile,
      setSafeTimeout,
      trackingClient,
    ],
  )

  const handleIntegrationFileSelected = tracked(_handleIntegrationFileSelected, { eventName: TrackEvent.DOCUMENT_PROSPECTING_FILE_ADDED })

  return (
    <div className='h-100 w-100 d-flex align-items-center justify-content-center'>
      <div className='d-flex flex-column align-items-center zocks-gap-2 document-prospecting-file-upload-container'>
        <div className='d-flex flex-column align-items-center zocks-gap-1 document-prospecting-file-upload-inner-container'>
          <div className='d-flex flex-column align-items-center zocks-gap-0.5'>
            <div className='py-1 zocks-px-0.75 zocks-bg-color-gray-100 border zocks-border-color-gray-200 rounded-5'>
              <span className='lead-extra-small zocks-text-gray-700'>Document Intelligence</span>
            </div>
            <h3>Turn Document into Contact</h3>
          </div>
          <span className='paragraph-medium text-center'>
            Your AI Assistant extracts contact details from the file and prepares them for review before saving to Zocks and your CRM.
          </span>
        </div>
        {!selectedFileName ? (
          <>
            <ProcessDocumentFileInput onFilesSelected={handleFilesSelected} />
            <DocumentIntegrationBrowseButtons onFilesSelected={handleIntegrationFileSelected} />
          </>
        ) : (
          <DocumentProspectingFileUploadProgress />
        )}
      </div>
    </div>
  )
}

const DocumentProspectingFileUploadProgress = () => {
  const selectedFileName = useAppSelector(selectDocumentProspectingSelectedFileName)
  const isUploading = useAppSelector(selectDocumentProspectingUploadIsLoading)
  const uploadedFileId = useAppSelector(selectDocumentProspectingUploadedFileId)
  const attachment = useAppSelector(selectDocumentProspectingAttachment)

  const baseFormValuesLoading = useAppSelector(selectDocumentProspectingBaseFormValuesLoading)
  const baseFormValues = useAppSelector(selectDocumentProspectingBaseFormValues)

  const isIntegration = attachment?.source === 'integration'
  const fileReady = isIntegration || !!uploadedFileId

  const progressItems: ProgressItem[] = useMemo(() => {
    if (!selectedFileName) {
      return []
    }
    const readyLabel = isIntegration ? `Selected ${selectedFileName}` : `Uploaded ${selectedFileName}`
    const pendingLabel = isIntegration ? `Selecting ${selectedFileName}…` : `Uploading ${selectedFileName}…`
    return [
      {
        isLoading: isUploading,
        isSuccess: !isUploading && fileReady,
        label: <ProgressItemWrapper isLoading={isUploading}>{fileReady ? readyLabel : pendingLabel}</ProgressItemWrapper>,
      },
      {
        isLoading: baseFormValuesLoading,
        isSuccess: !baseFormValuesLoading && !!baseFormValues,
        label: (
          <ProgressItemWrapper isLoading={baseFormValuesLoading}>
            {baseFormValues ? 'Contact details ready to review' : 'Extracting contact details…'}
          </ProgressItemWrapper>
        ),
      },
    ]
  }, [baseFormValues, baseFormValuesLoading, fileReady, isIntegration, isUploading, selectedFileName])

  return <ProcessDocumentFileUploadProgress items={progressItems} />
}
